import {
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readAudit } from '../api/endpoints';
import type { AuditEntry, Paginated } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useSort } from '../components/useSort';

const ACTION_COLOUR = (action: string): 'success' | 'warning' | 'default' => {
  if (action.endsWith('.disabled') || action.endsWith('.suspended')) {
    return 'warning';
  }

  if (action.endsWith('.enabled') || action.endsWith('.saved') || action.endsWith('.reactivated')) {
    return 'success';
  }

  return 'default';
};

/** The columns the API will order by. It matches these against a fixed list; anything else is ignored. */
type SortKey = 'createdAt' | 'actorAccountId' | 'action' | 'targetType';

/**
 * Every action the log can hold, from `AuditAction.type.ts`. A fixed list rather than the values present
 * in the current page: a filter offering only what is already on screen cannot find what is not.
 */
const ACTIONS = [
  'challenge.enabled',
  'challenge.disabled',
  'reading_story.saved',
  'subscription_plan.saved',
  'account.created',
  'account.password_reset',
  'account.role_changed',
  'account.suspended',
  'account.reactivated',
  'training.today.discarded',
  'training.today.reset-noop',
];

const TARGET_TYPES = ['account', 'challenge', 'child', 'reading_story', 'subscription_plan'];

export const AuditPage = () => {
  const [page, setPage] = useState(0);
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const sort = useSort<SortKey>('createdAt', 'desc', () => setPage(0));
  const { data, error, isLoading, refetch } = useQuery<Paginated<AuditEntry>>({
    queryKey: ['audit', page, sort.key, sort.direction, action, targetType],
    queryFn: () =>
      readAudit(page + 1, sort.key, sort.direction, action || undefined, targetType || undefined),
  });

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Audit"
        subtitle="Každá zmena vykonaná v back office, od najnovšej. Zapisuje sa v tej istej požiadavke, ktorá zmenu vykonala."
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            select
            label="Akcia"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {ACTIONS.map((one) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Cieľ"
            value={targetType}
            onChange={(event) => {
              setTargetType(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {TARGET_TYPES.map((one) => (
              <MenuItem key={one} value={one}>
                {one}
              </MenuItem>
            ))}
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="createdAt">
                  Kedy
                </SortableCell>
                <SortableCell sort={sort} column="actorAccountId">
                  Kto
                </SortableCell>
                <SortableCell sort={sort} column="action">
                  Akcia
                </SortableCell>
                <SortableCell sort={sort} column="targetType">
                  Cieľ
                </SortableCell>
                {/* Free-form JSON. There is no order it could be put in that would mean anything. */}
                <TableCell>Poznámka</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <TableSkeletonRows columns={5} /> : null}
              {!isLoading &&
                items.map((entry: AuditEntry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {new Date(entry.createdAt).toLocaleString('sk-SK')}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}
                      >
                        {entry.actorAccountId.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={entry.action}
                        color={ACTION_COLOUR(entry.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {entry.targetType}/{entry.targetId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{entry.metadata ?? '—'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && items.length === 0 ? (
          <EmptyState
            title="Zatiaľ žiadne zmeny"
            detail="Audit sa naplní, keď niekto zmení katalóg úloh, príbeh alebo plán predplatného."
          />
        ) : null}

        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={data?.pageSize ?? 25}
          rowsPerPageOptions={[data?.pageSize ?? 25]}
          labelRowsPerPage="Na stranu"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </Paper>
    </>
  );
};
